import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Calendar, Clock, Tag, Linkedin, ExternalLink } from 'lucide-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Image from '../../components/AppImage';

const resources = [
  {
    id: 1,
    title: "Data Governance Framework: From Data Chaos to Strategic Clarity",
    description: `Modern organisations are investing heavily in data platforms—cloud warehouses, dashboards, and machine learning capabilities. However, without a structured governance framework, these investments often fail to deliver consistent, trusted insights.\n\nAt Northstar Analytics, we view data governance as a business enabler, not a compliance exercise.\n\nThis framework provides a practical, implementation-ready model for establishing:\n• Clear ownership and accountability\n• Consistent data standards and policies\n• Scalable data quality and control mechanisms\n• Sustainable governance operating structures\n\n2. Governance Objectives\n\nThe primary objective of this framework is to ensure that data across the organisation is:\n\nTrusted – Accurate, consistent, and reliable\nAccountable – Clearly owned and managed\nAccessible – Available to the right people at the right time\nCompliant – Aligned with regulatory and security requirements\nActionable – Fit for analytics, reporting, and decision-making\n\n3. Guiding Principles\n\nNorthstar Analytics applies the following principles to all governance engagements:\n\nBusiness Ownership First – Data accountability resides with business domains—not IT\nGovernance Enables, Not Blocks – Frameworks are designed to support speed and agility\nStandardisation Where It Matters – Focus on critical data elements and high-impact domains\nEmbedded, Not Overlayed – Governance is integrated into existing workflows and platforms\nIterative Maturity – Start focused, scale progressively\n\n4. Governance Operating Model\n\nA clear operating model ensures governance is actionable and sustainable.\n\n4.1 Governance Structure\n\nData Governance Council\n• Executive-level oversight (CDO, CIO, Business Leads)\n• Defines strategy and approves policies\n• Resolves cross-functional conflicts\n\nData Owners (Business Accountability)\n• Senior stakeholders responsible for data domains (e.g., Finance, Sales)\n• Define data standards and business rules\n• Accountable for data quality outcomes\n\nData Stewards (Operational Governance)\n• Manage day-to-day data quality and definitions\n• Maintain metadata and documentation\n• Coordinate issue resolution\n\nData Custodians (Technical Enablement)\n• Responsible for infrastructure, pipelines, and storage\n• Ensure availability, performance, and security\n\n4.2 Responsibility Model (RACI)\n\n__RACI_TABLE__\n\n5. Core Governance Domains\n\n5.1 Data Quality Management\n\nObjective: Ensure data is fit for purpose across all analytical use cases\n\nFramework Components:\n• Defined quality dimensions (accuracy, completeness, consistency, timeliness)\n• Automated validation rules embedded in ELT pipelines\n• Data quality dashboards and alerts\n• Structured issue management workflow\n\nNorthstar Approach: We prioritise business-critical data elements (CDEs) to drive early impact.\n\n5.2 Metadata Management & Data Catalog\n\nObjective: Enable transparency, trust, and usability of data assets\n\nKey Capabilities:\n• Business definitions aligned to KPIs\n• Data lineage (source → transformation → output)\n• Ownership and stewardship mapping\n• Searchable data catalog interface\n\nOutcome: Reduced ambiguity and faster onboarding for analysts and stakeholders\n\n5.3 Data Security & Access Control\n\nObjective: Protect sensitive data while enabling appropriate access\n\nFramework Elements:\n• Role-Based Access Control (RBAC)\n• Data classification (public, internal, confidential, sensitive)\n• Audit logging and access monitoring\n• Compliance alignment (e.g., GDPR)\n\n5.4 Master Data Management (MDM)\n\nObjective: Establish a single, consistent view of key entities\n\nFocus Areas:\n• Customer, Product, Supplier, Location\n• Golden record definition\n• Deduplication and standardisation rules\n\nBusiness Impact: Improved reporting consistency and operational alignment\n\n5.5 Data Lifecycle Management\n\nGovernance spans the full data lifecycle:\n\nIngestion – Data contracts and validation\nStaging (Raw/Bronze) – Initial quality checks\nTransformation (Silver) – Standardisation and enrichment\nWarehouse (Gold) – Trusted analytical models\nConsumption – BI dashboards, ML models\nArchival/Deletion – Retention and compliance\n\n6. Governance Processes\n\nTo operationalise governance, the following workflows are implemented:\n\n6.1 Data Issue Management\n• Detection (automated/manual)\n• Logging (centralised system)\n• Assignment (Data Steward/Owner)\n• Resolution and root cause analysis\n\n6.2 New Data Source Onboarding\n• Source assessment (structure, quality, ownership)\n• Data contract definition\n• Integration into governance framework\n• Monitoring setup\n\n6.3 Change Management\n• Schema changes reviewed and approved\n• Impact analysis on downstream systems\n• Controlled deployment process\n\n6.4 Access Request Workflow\n• Role-based approval process\n• Automated provisioning where possible\n• Periodic access reviews\n\n7. Technology Enablement\n\nWhile governance is not tool-dependent, the following technologies support scale:\n\nTypical Stack:\n• Data Catalog: Collibra, Alation, DataHub\n• Data Quality: Great Expectations, Monte Carlo\n• Transformation & Lineage: dbt, BigQuery, Snowflake\n• Access Control: Cloud IAM frameworks\n\nNorthstar Positioning: Technology selection is driven by use case, scale, and existing ecosystem maturity.\n\n8. Governance Metrics & KPIs\n\nTo ensure continuous improvement, governance is measured through:\n• Data quality scores by domain\n• Number and severity of data incidents\n• Time to resolution (MTTR)\n• Data asset coverage (% governed vs total)\n• User trust and adoption metrics\n\n9. Implementation Roadmap (Northstar Approach)\n\nPhase 1: Current State Assessment\n• Data landscape review\n• Pain point identification\n• Stakeholder alignment\n\nPhase 2: Framework Design\n• Operating model definition\n• Policy and standards creation\n• Role assignment\n\nPhase 3: Pilot Implementation\n• Focus on high-impact domain (e.g., Customer or Revenue)\n• Deploy quality rules and governance processes\n• Establish quick wins\n\nPhase 4: Scale & Embed\n• Extend governance across domains\n• Integrate with BI and ML workflows\n• Introduce tooling where appropriate\n\n10. The Northstar Advantage\n\n• Business-first governance design\n• Pragmatic, implementation-led approach\n• Alignment with modern cloud architectures (Snowflake, BigQuery, Azure)\n• Focus on measurable outcomes—not theoretical models\n\nFinal Perspective\n\nData governance is often approached as a compliance requirement. In practice, it is a competitive advantage.\n\nOrganisations that get governance right:\n• Deliver consistent, trusted insights\n• Reduce operational inefficiencies\n• Enable faster, more confident decision-making\n\nAt Northstar Analytics, our role is to ensure governance becomes a driver of value, not overhead.`,
    type: "Framework",
    author: "Northstar Analytics",
    authorImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    publishDate: "March 2026",
    readTime: "20 min read",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop",
    tags: ["Governance", "Framework", "Data Strategy"],
  },
  {
    id: 2,
    title: "Building a Data-Driven Culture: Executive Playbook",
    description: `Strategic guide for C-level executives on fostering data literacy and analytical thinking across the organization. Covers change management, training programs, and success metrics.\n\nIncludes real-world case studies from Fortune 500 transformations.\n\nKey Topics Covered:\n\n1. Why Culture Matters More Than Technology\n\nMany organisations invest heavily in data tools but neglect the human side of transformation. Without cultural alignment, even the most sophisticated analytics platforms fail to deliver value.\n\nData literacy is not just a technical skill—it is an organisational capability that must be cultivated at every level, from the boardroom to the front line.\n\n2. The Executive's Role in Data Culture\n\nLeadership sets the tone. When executives model data-driven decision-making, it signals to the entire organisation that analytics matters.\n\nKey executive behaviours include:\n• Asking for data before making decisions\n• Celebrating data-driven wins publicly\n• Investing in data literacy programmes\n• Holding teams accountable to metrics\n\n3. Building Data Literacy Across the Organisation\n\nData literacy programmes should be tiered by role:\n\nExecutive Level: Strategic data interpretation, KPI ownership, data governance oversight\nManager Level: Dashboard proficiency, analytical thinking, data-driven planning\nOperational Level: Data entry quality, basic reporting, metric awareness\n\n4. Change Management for Data Transformation\n\nSuccessful data culture change requires structured change management:\n\n• Clear vision and communication\n• Executive sponsorship and visible commitment\n• Quick wins to build momentum\n• Ongoing training and support\n• Recognition and reward systems\n\n5. Measuring Cultural Progress\n\nKey indicators of a maturing data culture:\n• Percentage of decisions backed by data\n• Data literacy assessment scores\n• Dashboard adoption rates\n• Self-service analytics usage\n• Data quality improvement trends`,
    type: "Article",
    author: "Sarah Mitchell",
    authorImage: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
    publishDate: "December 3, 2024",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop",
    tags: ["Culture", "Leadership", "Strategy"],
  },
  {
    id: 3,
    title: "GDPR Compliance Checklist 2024",
    description: `Updated compliance checklist covering all GDPR requirements with practical implementation steps. Includes data mapping templates, consent management workflows, and breach response procedures.\n\nEssential for any organization handling EU personal data.\n\nSection 1: Lawful Basis for Processing\n\n• Identify and document the lawful basis for each processing activity\n• Consent must be freely given, specific, informed, and unambiguous\n• Legitimate interests assessments (LIA) required where applicable\n• Special category data requires explicit consent or specific legal basis\n\nSection 2: Data Subject Rights\n\nOrganisations must have processes to handle:\n• Right of access (Subject Access Requests within 30 days)\n• Right to rectification\n• Right to erasure ("right to be forgotten")\n• Right to data portability\n• Right to object to processing\n• Rights related to automated decision-making\n\nSection 3: Privacy by Design\n\n• Data minimisation: collect only what is necessary\n• Purpose limitation: use data only for stated purposes\n• Storage limitation: retain data only as long as necessary\n• Privacy impact assessments (DPIA) for high-risk processing\n\nSection 4: Data Breach Response\n\n• 72-hour notification requirement to supervisory authority\n• Individual notification when high risk to rights and freedoms\n• Breach register must be maintained\n• Response plan and escalation procedures required\n\nSection 5: Third-Party Data Processors\n\n• Data Processing Agreements (DPAs) required with all processors\n• Due diligence on processor security measures\n• International transfer mechanisms (SCCs, adequacy decisions)\n• Regular processor audits recommended\n\nSection 6: Documentation Requirements\n\n• Records of Processing Activities (RoPA)\n• Privacy notices and consent records\n• DPIA documentation\n• Data retention schedules\n• Staff training records`,
    type: "Tool",
    author: "Emma Rodriguez",
    authorImage: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    publishDate: "November 28, 2024",
    readTime: "12 min read",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=400&fit=crop",
    tags: ["GDPR", "Compliance", "Checklist"],
  },
  {
    id: 4,
    title: "Advanced Analytics ROI Calculator",
    description: `Interactive tool for calculating the return on investment for analytics initiatives. Includes cost modeling, benefit quantification, and risk assessment components.\n\nHelps build business cases for data and analytics investments.\n\nHow to Use This Calculator\n\nStep 1: Define Your Investment\n\nInput your total analytics investment including:\n• Technology costs (licences, infrastructure, cloud)\n• Implementation costs (consulting, development)\n• Training and change management\n• Ongoing operational costs\n\nStep 2: Quantify Benefits\n\nAnalytics ROI comes from multiple value streams:\n\nRevenue Enhancement:\n• Improved customer targeting and conversion\n• Reduced churn through predictive analytics\n• New product/service opportunities identified\n• Pricing optimisation\n\nCost Reduction:\n• Operational efficiency improvements\n• Reduced manual reporting effort\n• Better inventory and resource management\n• Fraud detection and prevention\n\nRisk Mitigation:\n• Regulatory compliance cost avoidance\n• Improved audit readiness\n• Early warning systems for operational issues\n\nStep 3: Apply Time Value\n\nBenefits are typically realised over 3-5 years. Apply appropriate discount rates to calculate Net Present Value (NPV) and Internal Rate of Return (IRR).\n\nStep 4: Sensitivity Analysis\n\nTest your assumptions with optimistic, base case, and pessimistic scenarios to understand the range of potential outcomes.\n\nBenchmark ROI Ranges:\n• Basic BI implementation: 150-300% over 3 years\n• Advanced analytics platform: 200-500% over 3 years\n• AI/ML capabilities: 300-800% over 5 years (higher risk, higher reward)`,
    type: "Tool",
    author: "David Park",
    authorImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    publishDate: "November 25, 2024",
    readTime: "10 min read",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop",
    tags: ["ROI", "Analytics", "Calculator"],
  },
  {
    id: 5,
    title: "Data Quality Assessment Webinar",
    description: `Live webinar covering data quality fundamentals, assessment methodologies, and improvement strategies. Features interactive Q&A and real-world examples.\n\nScheduled for December 15, 2024 at 2:00 PM GMT.\n\nWebinar Overview\n\nThis 60-minute session will cover:\n\nPart 1: Data Quality Fundamentals (15 minutes)\n• The six dimensions of data quality: accuracy, completeness, consistency, timeliness, validity, uniqueness\n• Why data quality matters for analytics and AI\n• Common root causes of data quality issues\n• The cost of poor data quality\n\nPart 2: Assessment Methodology (20 minutes)\n• How to conduct a data quality assessment\n• Profiling techniques and tools\n• Scoring and prioritisation frameworks\n• Stakeholder engagement in quality assessment\n\nPart 3: Improvement Strategies (15 minutes)\n• Data quality rules and validation\n• Automated monitoring and alerting\n• Root cause analysis and remediation\n• Building a data quality culture\n\nPart 4: Live Q&A (10 minutes)\n• Interactive questions from attendees\n• Real-world examples and case studies\n\nWho Should Attend:\n• Data engineers and architects\n• Analytics managers and leads\n• Data governance professionals\n• Business intelligence teams\n• Anyone responsible for data quality outcomes`,
    type: "Webinar",
    author: "Lisa Thompson",
    authorImage: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face",
    publishDate: "December 15, 2024",
    readTime: "60 min session",
    image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=400&fit=crop",
    tags: ["Data Quality", "Webinar", "Live"],
  },
  {
    id: 6,
    title: "Healthcare Data Analytics: Regulatory Compliance Guide",
    description: `Specialized guide for healthcare organizations navigating HIPAA, FDA, and other regulatory requirements while implementing analytics solutions.\n\nIncludes templates for risk assessments and compliance documentation.\n\nSection 1: HIPAA Compliance for Analytics\n\nThe Health Insurance Portability and Accountability Act (HIPAA) sets strict requirements for handling Protected Health Information (PHI).\n\nKey requirements for analytics environments:\n• De-identification of PHI before use in analytics (Safe Harbor or Expert Determination method)\n• Business Associate Agreements (BAAs) with all analytics vendors\n• Minimum necessary standard for data access\n• Audit controls and access logging\n• Encryption of PHI at rest and in transit\n\nSection 2: FDA Requirements for Clinical Analytics\n\nFor organisations conducting clinical trials or using analytics in regulated medical contexts:\n• 21 CFR Part 11 compliance for electronic records\n• Validation requirements for analytical systems\n• Audit trail requirements\n• Data integrity controls\n\nSection 3: Real-World Evidence (RWE) Analytics\n\nHealthcare organisations increasingly use real-world data for:\n• Post-market surveillance\n• Comparative effectiveness research\n• Health economics and outcomes research (HEOR)\n• Population health management\n\nSection 4: Data Governance in Healthcare\n\nHealthcare-specific governance considerations:\n• Clinical data stewardship roles\n• Master Patient Index (MPI) management\n• ICD coding standards and terminology management\n• Interoperability requirements (HL7 FHIR)\n\nSection 5: Risk Assessment Templates\n\nIncluded templates:\n• HIPAA Risk Assessment Worksheet\n• Vendor Due Diligence Checklist\n• Data Classification Matrix for Healthcare\n• Incident Response Plan Template`,
    type: "Article",
    author: "Dr. James Wilson",
    authorImage: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=150&h=150&fit=crop&crop=face",
    publishDate: "November 20, 2024",
    readTime: "14 min read",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=400&fit=crop",
    tags: ["Healthcare", "HIPAA", "Compliance"],
  },
];

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const resource = resources?.find(r => r?.id === parseInt(id, 10));

  const relatedArticles = resources?.filter(r => r?.id !== parseInt(id, 10))?.slice(0, 3);

  const handleLinkedInShare = () => {
    const articleUrl = encodeURIComponent(window.location?.href);
    const title = encodeURIComponent(resource?.title || '');
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${articleUrl}&title=${title}`;
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const siteUrl = 'https://northstaranalytics.eu';
  const articleUrl = resource ? `${siteUrl}/article/${resource?.id}` : siteUrl;

  // Support both static (hardcoded) and dynamic (Supabase) article field names
  const articleTitle = resource?.title || '';
  const articleAuthor = resource?.author || resource?.author_name || 'Northstar Analytics';
  const articleAuthorImage = resource?.authorImage || resource?.author_image || '';
  const articlePublishDate = resource?.publishDate || resource?.publish_date || '';
  const articleReadTime = resource?.readTime || resource?.read_time || '';
  const articleImage = resource?.cover_image || resource?.image || `${siteUrl}/assets/images/northstar-logo-light-final-1769001176478.png`;
  const articleTags = resource?.tags || [];
  const articleType = resource?.type || resource?.content_type || 'Article';

  // Build description: prefer excerpt field (Supabase), fallback to first paragraph of description/content
  const rawBody = resource?.description || resource?.content || '';
  const articleExcerpt = resource?.excerpt
    ? resource?.excerpt?.slice(0, 200)?.trim()
    : rawBody?.split('\n\n')?.[0]?.slice(0, 200)?.trim() || '';

  const ogTitle = articleTitle ? `${articleTitle} | Northstar Analytics` : 'Northstar Analytics';
  const ogDescription = articleExcerpt || 'Expert data strategy, governance, and analytics consulting.';
  const ogImage = articleImage;

  // ISO date for JSON-LD (handle both "March 2026" and ISO strings)
  const isoDate = (() => {
    if (!articlePublishDate) return new Date()?.toISOString();
    const parsed = new Date(articlePublishDate);
    return isNaN(parsed?.getTime()) ? new Date()?.toISOString() : parsed?.toISOString();
  })();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleTitle,
    description: ogDescription,
    image: ogImage,
    url: articleUrl,
    datePublished: isoDate,
    dateModified: isoDate,
    author: {
      '@type': 'Person',
      name: articleAuthor,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Northstar Analytics',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/assets/images/northstar-logo-light-final-1769001176478.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    keywords: articleTags?.join(', '),
    articleSection: articleType,
  };

  if (!resource) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-24 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/knowledge-hub-thought-leadership-center" className="inline-flex items-center text-teal-600 hover:text-teal-800 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Knowledge Hub
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const paragraphs = resource?.description?.split('\n\n');

  const RACITable = () => {
    const raciData = [
      { activity: 'Define Standards',  owner: 'A', steward: 'R', custodian: 'C', council: 'C' },
      { activity: 'Monitor Quality',   owner: 'A', steward: 'R', custodian: 'C', council: 'I' },
      { activity: 'Data Pipelines',    owner: 'I', steward: 'C', custodian: 'R', council: 'I' },
      { activity: 'Issue Resolution',  owner: 'A', steward: 'R', custodian: 'C', council: 'C' },
      { activity: 'Policy Approval',   owner: 'C', steward: 'C', custodian: 'I', council: 'A' },
    ];

    const badgeStyle = (letter) => {
      const styles = {
        R: 'bg-teal-100 text-teal-800 font-bold',
        A: 'bg-blue-100 text-blue-800 font-bold',
        C: 'bg-amber-100 text-amber-800 font-bold',
        I: 'bg-gray-100 text-gray-600 font-medium',
      };
      return styles?.[letter] || 'bg-gray-100 text-gray-600';
    };

    return (
      <div className="my-6 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-900 text-white">
              <th className="px-5 py-3 text-left font-semibold">Activity</th>
              <th className="px-5 py-3 text-center font-semibold">Data Owner</th>
              <th className="px-5 py-3 text-center font-semibold">Data Steward</th>
              <th className="px-5 py-3 text-center font-semibold">Data Custodian</th>
              <th className="px-5 py-3 text-center font-semibold">Governance Council</th>
            </tr>
          </thead>
          <tbody>
            {raciData?.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-5 py-3 font-medium text-gray-800">{row?.activity}</td>
                {[row?.owner, row?.steward, row?.custodian, row?.council]?.map((val, j) => (
                  <td key={j} className="px-5 py-3 text-center">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs ${badgeStyle(val)}`}>
                      {val}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex flex-wrap gap-4 px-5 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
          <span><span className="inline-block px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 font-bold mr-1">R</span>Responsible</span>
          <span><span className="inline-block px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-bold mr-1">A</span>Accountable</span>
          <span><span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold mr-1">C</span>Consulted</span>
          <span><span className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium mr-1">I</span>Informed</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta name="keywords" content={articleTags?.join(', ')} />
        <link rel="canonical" href={articleUrl} />

        {/* Open Graph / LinkedIn */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={articleTitle} />
        <meta property="og:site_name" content="Northstar Analytics" />
        <meta property="og:locale" content="en_GB" />

        {/* Article-specific OG */}
        <meta property="article:author" content={articleAuthor} />
        <meta property="article:published_time" content={isoDate} />
        <meta property="article:section" content={articleType} />
        {articleTags?.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:image:alt" content={articleTitle} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>
      <Header />
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-28 lg:pt-32">
        <Link
          to="/knowledge-hub-thought-leadership-center"
          className="inline-flex items-center text-teal-600 hover:text-teal-800 transition-colors mb-8 group font-medium"
        >
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Knowledge Hub
        </Link>

        <header className="mb-10">
          <div className="mb-4">
            <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
              {articleType}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
            {articleTitle}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 border-t border-b border-gray-200 py-4 mb-6">
            <div className="flex items-center gap-2">
              <Image
                src={articleAuthorImage}
                alt={articleAuthor}
                className="w-8 h-8 rounded-full object-cover"
              />
              <span className="font-medium">{articleAuthor}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-teal-600" />
              <span>{articlePublishDate}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-teal-600" />
              <span>{articleReadTime}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {articleTags?.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm"
              >
                <Tag className="w-3 h-3" />
                {tag}
              </span>
            ))}
          </div>

          <Image
            src={articleImage}
            alt={articleTitle}
            className="w-full h-64 md:h-80 object-cover rounded-xl"
          />
        </header>

        <div className="prose prose-lg max-w-none text-gray-700 space-y-5">
          {paragraphs?.map((para, index) => {
            if (para?.trim() === '__RACI_TABLE__') {
              return <RACITable key={index} />;
            }
            if (para?.trim()?.startsWith('•')) {
              const items = para?.split('\n')?.filter(line => line?.trim());
              return (
                <ul key={index} className="list-disc pl-6 space-y-2">
                  {items?.map((item, i) => (
                    <li key={i} className="leading-relaxed">{item?.replace(/^•\s*/, '')}</li>
                  ))}
                </ul>
              );
            }
            const lines = para?.split('\n');
            if (lines?.length > 1 && !para?.startsWith(' ')) {
              return (
                <div key={index} className="space-y-2">
                  {lines?.map((line, i) => {
                    if (!line?.trim()) return null;
                    if (line?.trim()?.startsWith('•')) {
                      return <p key={i} className="pl-4 leading-relaxed">• {line?.replace(/^•\s*/, '')}</p>;
                    }
                    if (i === 0 && lines?.length > 1 && /^\d+\.|^[A-Z]/?.test(line?.trim())) {
                      return <p key={i} className="font-semibold text-gray-900 leading-relaxed">{line}</p>;
                    }
                    return <p key={i} className="leading-relaxed">{line}</p>;
                  })}
                </div>
              );
            }
            return (
              <p key={index} className="leading-relaxed">
                {para}
              </p>
            );
          })}
        </div>

        {/* LinkedIn Share Button */}
        <div className="mt-10 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-3 font-medium">Share this article</p>
          <button
            onClick={handleLinkedInShare}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A66C2] hover:bg-[#004182] text-white font-medium rounded-lg transition-colors text-sm"
          >
            <Linkedin className="w-4 h-4" />
            Share on LinkedIn
          </button>
        </div>

        {/* Consultation CTA */}
        <div className="mt-10 p-8 bg-gray-900 rounded-xl text-white">
          <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Data Strategy?</h3>
          <p className="text-gray-300 mb-6">
            Our team specialises in helping organisations build robust data capabilities that deliver measurable business outcomes.
          </p>
          <Link
            to="/schedule-consultation"
            className="inline-block px-6 py-3 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-lg transition-colors"
          >
            Schedule a Free Consultation
          </Link>
        </div>

        {/* Related Articles */}
        {relatedArticles?.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles?.map((article) => (
                <Link
                  key={article?.id}
                  to={`/article/${article?.id}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col"
                >
                  <div className="overflow-hidden h-40">
                    <Image
                      src={article?.image}
                      alt={article?.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full w-fit mb-3">
                      {article?.type}
                    </span>
                    <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-3 group-hover:text-teal-700 transition-colors line-clamp-2">
                      {article?.title}
                    </h3>
                    <div className="mt-auto flex items-center justify-between text-xs text-gray-500">
                      <span>{article?.publishDate}</span>
                      <span className="flex items-center gap-1 text-teal-600 font-medium">
                        Read <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
};

export default ArticleDetail;
